import { createRouter, createWebHistory } from "vue-router";
import type { RouteLocation, RouteLocationNormalized, RouteRecordRaw, NavigationGuardNext } from "vue-router";
import { useAuthStore } from '@/store/auth.ts'
import {
    holdBillingInvitationTokenForLogin,
    parseBillingInvitationToken,
    stripBillingInvitationFromFullPath,
} from '@/app/composables/expert-billing/expertCheckout'

import ExpertLayout from "@/layouts/ExpertLayout.vue";
import ExpertDashboard from "@/pages/expert/Dashboard.vue";
import ExpertLeadsV2 from "@/app/pages/ExpertLeadsV2.vue";
import ExpertListing from "@/pages/expert/MyListing.vue";
import ExpertReviews from "@/app/pages/ExpertReviews.vue";
import ExpertWallet from "@/pages/expert/Wallet.vue";
import ExpertChatroom from "@/components/expert/cards/Chatroom.vue";

import AdminLayout from "@/layouts/AdminLayout.vue";
import AdminDashboard from "@/pages/admin/Dashboard.vue";
import AdminNewListings from "@/pages/admin/NewListings.vue";
import AdminClients from "@/pages/admin/Clients.vue";
import AdminQuoteSent from "@/pages/admin/QuoteSent.vue";
import AdminReferrals from "@/pages/admin/Referrals.vue";
import AdminTransactions from "@/pages/admin/Transactions.vue";
import AdminAppsThemes from "@/pages/admin/AppsThemes.vue";
import AdminExpertSubscriptions from "@/app/pages/AdminExpertSubscriptions.vue";
import AdminExpertLevels from "@/pages/admin/ExpertLevels.vue";
import AdminWalletTransactions from "@/pages/admin/WalletTransactions.vue";
import AdminReviews from "@/app/pages/AdminReviews.vue";
import AdminEmailPreview from "@/pages/admin/EmailPreview.vue";
import AdminExpertManager from "@/app/pages/ExpertManager.vue";
import AdminExpertDetail from "@/app/pages/ExpertDetail.vue";
import AdminAiSummariesPage from "@/app/pages/AdminAiSummariesPage.vue";
import AdminExpertClaimsPage from "@/app/pages/AdminExpertClaimsPage.vue";
import AdminConfidenceSignals from "@/app/pages/AdminConfidenceSignalsPage.vue";
import AdminCategoriesManager from "@/app/pages/CategoriesManager.vue";
import AdminSatelliteContactSites from "@/app/pages/SatelliteContactSitesPage.vue";
import AdminBanList from "@/app/pages/AdminBanListPage.vue";
import AdminEmails from "@/app/pages/AdminEmailsPage.vue";
import AdminPartnerDirectoryScraper from "@/app/pages/PartnerDirectoryScraperPage.vue";
import AdminSettingsPage from "@/app/pages/AdminSettingsPage.vue";
import AdminLeadsProjectsPage from "@/app/pages/AdminLeadsProjectsPage.vue";
import AdminProjectDetailPage from "@/app/pages/AdminProjectDetailPage.vue";
import { PageSchemasPage as AdminPages, SchemaBuilderPage as AdminSchemaBuilder, PageMetafieldsEditorPage as AdminPageMetafieldsEditor, RedirectsPage as AdminRedirects, CustomizedPagesPage as AdminCustomizedPages, PageCollectionsPage as AdminPageCollections, PageCollectionEditorPage as AdminPageCollectionEditor } from "@shopexperts/admin-pages-editor";
import { LeadsManagementPage as AdminLeadsManager } from "@shopexperts/admin-leads-manager";
// import ClientTransactions from "@/pages/client/Transactions.vue";
// import ClientSettings from "@/pages/client/Settings.vue";

// import ClientLayout from "@/layouts/ClientLayout.vue";
// import ClientDashboard from "@/pages/client/Dashboard.vue";
// import ClientRequest from "@/pages/client/MyRequests.vue";
// import PackagedServices from "@/pages/client/PackagedServices.vue";
// import ClientSignup from "@/pages/client/ClientSignup.vue";
// import ClientFreeQuote from "@/pages/client/ClientFreeQuote.vue";
// import PreferredExpert from "@/components/client/freeQuote/PreferredExpert.vue";
// import StoreOrAgencyDetails from "@/components/client/freeQuote/StoreOrAgencyDetails.vue";
// import ProjectBrief from "@/components/client/freeQuote/ProjectBrief.vue";
// import AccountInfo from "@/components/client/freeQuote/AccountInfo.vue";

import UnifiedLogin from "@/components/auth/UnifiedLogin.vue"
// import Reviews from "@/pages/client/Reviews.vue"
import ForgotPassword from "@/pages/ForgotPassword.vue";
import ResetPassword from "@/pages/ResetPassword.vue";
import CreatePassword from "@/pages/CreatePassword.vue";
import ExpertSignup from "@/pages/expert/ExpertSignup.vue";
// import GetMatched from "@/pages/client/GetMatched.vue";
// import GetMatchedDetails from "@/components/client/getMatched/GetMatchedDetails.vue";
// import GetMatchedBrief from "@/components/client/getMatched/GetMatchedBrief.vue";
// import GetMatchedAccount from "@/components/client/getMatched/GetMatchedAccount.vue";
import LeadDetailsPage from "@/pages/expert/lead/LeadDetailsPage.vue";
import SignupStart from "@/components/expert/SignUp/SignupStart.vue";
import ContactInfo from "@/components/expert/SignUp/ContactInfo.vue";
import ProfessionalDetails from "@/components/expert/SignUp/ProfessionalDetails.vue";
import Experience from "@/components/expert/SignUp/Experience.vue";
import JoinNetworkMessage from "@/components/expert/SignUp/JoinNetworkMessage.vue";
import OnBoardingIntro from "@/pages/expert/onboarding/OnBoardingIntro.vue";

import SSOLogin from "@/pages/client/SSOLogin.vue";
import ListingEditorLayout from "@/pages/expert/listing/ListingEditorLayout.vue";
import PublicReviewInvite from "@/app/pages/PublicReviewInvite.vue";
import CreateProject from "@/app/pages/CreateProject.vue";
import ClientSignup from "@/app/pages/ClientSignup.vue";
import EmbeddedFreeConsultation from "@/app/pages/EmbeddedFreeConsultation.vue";
import EmbeddedClaimListing from "@/app/pages/EmbeddedClaimListing.vue";
import ClaimVerify from "@/app/pages/ClaimVerify.vue";

// Client Dashboard (new app structure)
import ClientDashboardLayout from "@/app/layouts/ClientDashboardLayout.vue";
import ExpertSettings from "@/app/pages/ExpertSettings.vue";
import ClientOverview from "@/app/pages/ClientOverview.vue";
import ClientLeads from "@/app/pages/ClientLeads.vue";
import ClientReviews from "@/app/pages/ClientReviews.vue";
import ClientDirectReview from "@/app/pages/ClientDirectReview.vue";
import EmbeddedDirectReview from "@/app/pages/EmbeddedDirectReview.vue";
import ClientSettings from "@/app/pages/ClientSettings.vue";
import VerifyEmailChange from "@/app/pages/VerifyEmailChange.vue";
import VerifyEmail from "@/app/pages/VerifyEmail.vue";
import AcceptInvite from "@/app/pages/AcceptInvite.vue";
import ProjectsListPage from "@/v2Projects/pages/ProjectsListPage.vue";
import DirectMessagesPage from "@/v2Projects/pages/DirectMessagesPage.vue";
import DirectMessageStartPage from "@/v2Projects/pages/DirectMessageStartPage.vue";
import ProjectDetailPage from "@/v2Projects/pages/ProjectDetailPage.vue";
import QuoteCheckoutPage from "@/v2Projects/pages/QuoteCheckoutPage.vue";
import { useFeatureFlags } from "@/app/composables/useFeatureFlags";

const isV2ProjectsEnabled = (): boolean => {
  const { isEnabled } = useFeatureFlags();
  return isEnabled("enable_v2_projects");
};

const getClientHomePath = (): string => "/client/overview";
const expertSignupAccessKey = "SHPXPRT26";
const expertSignupAccessStorageKey = "shopexperts.expertSignupAccessGranted";

export const embeddedRouteMeta = {
    robots: 'noindex',
    embedded: true,
} as const;

const redirectToProjectsWhenEnabled = (targetPath: string) => (
  to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext,
) => {
  if (isV2ProjectsEnabled()) {
    next({
      path: targetPath,
      query: to.query,
      hash: to.hash,
    });
    return;
  }
  next();
};

const hasExpertSignupAccess = (to: RouteLocationNormalized): boolean => {
  const key = Array.isArray(to.query.key) ? to.query.key[0] : to.query.key;

  if (key === expertSignupAccessKey) {
    sessionStorage.setItem(expertSignupAccessStorageKey, "true");
    return true;
  }

  return sessionStorage.getItem(expertSignupAccessStorageKey) === "true";
};

const requireExpertSignupAccess = (
  to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext,
) => {
  const { isEnabled } = useFeatureFlags();

  if (!isEnabled("require_expert_signup_access_key") || to.name === "expert-signup-join-network" || hasExpertSignupAccess(to)) {
    next();
    return;
  }

  next({
    name: "expert-signup-join-network",
  });
};

const requireV2Projects = (
  to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext,
) => {
  const { isEnabled } = useFeatureFlags();
  if (!isEnabled("enable_v2_projects")) {
    const fallback = to.path.startsWith("/expert") ? "/expert/dashboard" : "/client/overview";
    next(fallback);
    return;
  }
  next();
};

const requireExpertWallet = (
  to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext,
) => {
  const { isExpertWalletEnabled } = useFeatureFlags();
  if (!isExpertWalletEnabled(to.query)) {
    next("/expert/dashboard");
    return;
  }
  next();
};

const updateRobotsMeta = (content?: string) => {
    const selector = 'meta[name="robots"][data-router-managed="true"]';
    const existingTag = document.head.querySelector<HTMLMetaElement>(selector);

    if (!content) {
        existingTag?.remove();
        return;
    }

    const tag = existingTag ?? document.createElement('meta');
    tag.name = 'robots';
    tag.content = content;
    tag.dataset.routerManaged = 'true';

    if (!existingTag) {
        document.head.appendChild(tag);
    }
};

const updateDocumentMeta = (to: RouteLocationNormalized) => {
    document.title = (to.meta.title as string) || 'shopexperts';
    updateRobotsMeta(to.meta.robots as string | undefined);
};

export const redirectToDashboard = (user: any, router: any) => {
    if (user.role_id === 1) {
        router.push('/admin/dashboard')
    } else if (user.role_id === 2) {
        router.push(getClientHomePath())
    } else if (user.role_id === 3) {
        router.push('/expert/dashboard')
    }
}

const routes = [
    {
        path: '/',
        name: 'home',
        component: UnifiedLogin,
        meta: {
            title: 'shopexperts',
        }
    },
    {
        path: '/login',
        name: 'login',
        component: UnifiedLogin,
        meta: {
            title: 'shopexperts - Login',
        }
    },
    {
        path: '/expert/login',
        name: 'expert-login',
        component: UnifiedLogin,
        meta: {
            title: 'shopexperts - Login',
        }
    },
    {
        path: '/client/login',
        name: 'client-login',
        component: UnifiedLogin,
        meta: {
            title: 'shopexperts - Login',
        }
    },
    {
        path: '/admin/login',
        redirect: '/login',
    },
    {
        path: '/sso-login',
        name: 'SSOLogin',
        component: SSOLogin,
    },
    {
        path: '/checkout',
        name: 'quote-checkout',
        component: QuoteCheckoutPage,
        meta: {
            title: 'shopexperts - Secure Checkout',
        },
    },
    {
        path: '/forgot-password',
        name: 'forgot-password',
        component: ForgotPassword,
        meta: {
            title: 'shopexperts - Forgot Password',
        }
    },
    {
        path: '/reset-password',
        name: 'reset-password',
        component: ResetPassword,
        meta: {
            title: 'shopexperts - Reset Password',
        }
    },
    {
        path: '/create-password',
        name: 'create-password',
        component: CreatePassword,
        meta: {
            title: 'shopexperts - Create Password',
        }
    },
    {
        path: '/accept-invite',
        name: 'accept-invite',
        component: AcceptInvite,
        meta: {
            title: 'shopexperts - Accept Invitation',
        }
    },
    {
        path: '/logout',
        name: 'logout',
        beforeEnter: (_to: RouteLocationNormalized, _from: RouteLocationNormalized, next: NavigationGuardNext) => {
            const authStore = useAuthStore()
            authStore.logout()
            next('/login')
        },
        component: UnifiedLogin, // Fallback component (won't be rendered due to redirect)
        meta: {
            title: 'shopexperts - Logout',
        }
    },
    {
        path: '/review/invite',
        name: 'review-invite',
        component: PublicReviewInvite,
        meta: {
            title: 'shopexperts - Write Review',
        }
    },
    {
        path: '/get-free-quote',
        name: 'leads-form',
        component: CreateProject,
        meta: {
            title: 'shopexperts - Get Free Quote',
            robots: 'noindex',
        }
    },
    {
        path: '/embedded/free-consultation',
        name: 'embedded-free-consultation',
        component: EmbeddedFreeConsultation,
        meta: {
            ...embeddedRouteMeta,
            title: 'shopexperts - Book a Free Consultation',
        }
    },
    {
        path: '/embedded/direct-review',
        name: 'embedded-direct-review',
        component: EmbeddedDirectReview,
        meta: {
            ...embeddedRouteMeta,
            title: 'shopexperts - Write a Review',
        }
    },
    {
        path: '/embedded/claim-listing',
        name: 'embedded-claim-listing',
        component: EmbeddedClaimListing,
        meta: {
            ...embeddedRouteMeta,
            title: 'shopexperts - Claim this listing',
        }
    },
    {
        path: '/create-project',
        redirect: '/get-free-quote',
    },
    {
        path: '/client/signup',
        name: 'client-signup',
        component: ClientSignup,
        meta: {
            title: 'shopexperts - Client Signup',
        }
    },
    // {
    //     path: "/client/signup",
    //     name: "client-signup",
    //     component: ClientSignup,
    //     meta: {
    //         title: 'shopexperts - Client Signup',
    //     }
    // },
    {
        path: "/expert/signup",
        component: ExpertSignup,
        beforeEnter: requireExpertSignupAccess,
        meta: {
            title: 'shopexperts - Expert Signup',
        },
        children: [
            {
                path: "join-network",
                name: "expert-signup-join-network",
                component: JoinNetworkMessage,
                meta: {
                    title: 'shopexperts - Join Our Network',
                }
            },
            {
                path: "",
                name: "expert-signup-start",
                component: SignupStart
            },
            {
                path: "contact-info",
                name: "expert-signup-contact",
                component: ContactInfo
            },
            {
                path: "professional-details",
                name: "expert-signup-professional",
                component: ProfessionalDetails
            },
            {
                path: "experience",
                name: "expert-signup-experience",
                component: Experience
            },
        ],
    },
    // {
    //     path: "/client/free-quote",
    //     component: ClientFreeQuote,
    //     meta: {
    //         title: 'shopexperts - Client Quote Request',
    //     },
    //     children: [
    //         {
    //             path: "",
    //             name: "client-claim-expert",
    //             component: PreferredExpert
    //         },
    //         {
    //             path: "agency-details",
    //             name: "store-details",
    //             component: StoreOrAgencyDetails
    //         },
    //         {
    //             path: "project-brief",
    //             name: "project-brief",
    //             component: ProjectBrief
    //         },
    //         {
    //             path: "account-info",
    //             name: "account-info",
    //             component: AccountInfo
    //         }
    //     ]
    // },
    // {
    //     path: "/client/get-matched",
    //     component: GetMatched,
    //     meta: {
    //         title: 'shopexperts - Client Get Matched',
    //     },
    //     children: [
    //         {
    //             path: "",
    //             name: "get-matched-details",
    //             component: GetMatchedDetails
    //         },
    //         {
    //             path: "project-brief",
    //             name: "get-matched-brief",
    //             component: GetMatchedBrief
    //         },
    //         {
    //             path: "account-info",
    //             name: "get-matched-account",
    //             component: GetMatchedAccount
    //         }
    //     ]
    // },
    {
        path: '/expert/listing',
        component: ListingEditorLayout,
    },
    {
        path: "/expert",
        component: ExpertLayout,
        meta: {
            title: 'shopexperts - Expert Dashboard',
            requiresAuth: true,
            requiredRole: 'expert'
        },
        children: [
            {
                path: 'onboarding',
                name: 'expert-onboarding-intro',
                component: OnBoardingIntro,
                meta: {
                    title: 'shopexperts - Expert Onboarding',
                },
            },
            {
                path: "dashboard",
                name: "expert-dashboard",
                component: ExpertDashboard,
                meta: {
                    title: 'shopexperts - Expert Dashboard',
                },
            },
            {
                path: "leads",
                name: "expert-leads",
                component: ExpertLeadsV2,
                beforeEnter: redirectToProjectsWhenEnabled("/expert/projects"),
                meta: {
                    title: 'shopexperts - Expert Leads',
                },
            },
            {
                path: "messages/:threadId?",
                name: "expert-messages",
                component: DirectMessagesPage,
                beforeEnter: requireV2Projects,
                meta: { title: 'shopexperts - Messages' },
            },
            {
                path: "projects",
                name: "expert-projects",
                component: ProjectsListPage,
                beforeEnter: requireV2Projects,
                meta: {
                    title: 'shopexperts - Expert Leads & Projects',
                },
            },
            {
                path: "projects/:id",
                name: "expert-project-details",
                component: ProjectDetailPage,
                beforeEnter: requireV2Projects,
                meta: {
                    title: 'shopexperts - Project Details',
                },
            },
            {
                path: "lead/:leadId",
                name: "expert-lead-collection-changes",
                component: LeadDetailsPage,
                meta: {
                    title: 'shopexperts - Lead Details',
                },
            },
            {
                path: "lead/:leadId/chatroom",
                name: "expert-lead-chatroom",
                component: ExpertChatroom,
                meta: {
                    title: 'shopexperts - Lead Details',
                },
            },
            {
                path: "my-listings",
                name: "expert-my-listings",
                component: ExpertListing,
                meta: {
                    title: 'shopexperts - Expert Listings',
                },
            },
            {
                path: "reviews",
                name: "expert-reviews",
                component: ExpertReviews,
                meta: {
                    title: 'shopexperts - Expert Reviews',
                },
            },
            {
                path: "wallet",
                name: "expert-wallet",
                component: ExpertWallet,
                beforeEnter: requireExpertWallet,
                meta: {
                    title: 'shopexperts - Wallet',
                },
            },
            {
                path: "settings",
                name: "expert-settings",
                component: ExpertSettings,
                meta: {
                    title: 'shopexperts - Expert Settings',
                },
            },
        ]
    },
    {
        path: "/admin",
        component: AdminLayout,
        meta: {
            title: 'shopexperts - Admin', 
            requiresAuth: true, 
            requiredRole: 'admin' 
        },
        children: [
            {
                path: "settings",
                name: "admin-settings",
                component: AdminSettingsPage,
                meta: {
                    title: 'shopexperts - Admin Settings',
                },
            },
            {
                path: "ai-summaries",
                name: "admin-ai-summaries",
                component: AdminAiSummariesPage,
                meta: {
                    title: 'shopexperts - AI Summaries',
                },
            },
            {
                path: "dashboard",
                name: "admin-dashboard",
                component: AdminDashboard,
                meta: {
                    title: 'shopexperts - Admin Dashboard',
                },
            },
            {
                path: "new-listings",
                name: "admin-listings",
                component: AdminNewListings,
                meta: {
                    title: 'shopexperts - Admin Listings',
                },
            },
            {
                path: "clients",
                name: "admin-clients",
                component: AdminClients,
            },
            {
                path: "leads-projects",
                name: "admin-leads-projects",
                component: AdminLeadsProjectsPage,
                meta: {
                    title: 'shopexperts - Leads & Projects Manager',
                },
            },
            {
                path: "leads-projects/projects/:id",
                name: "admin-project-detail",
                component: AdminProjectDetailPage,
                meta: {
                    title: 'shopexperts - Admin Project Workspace',
                },
            },
            {
                path: "leads-manager",
                name: "admin-leads-manager",
                component: AdminLeadsManager,
                meta: {
                    title: 'shopexperts - Leads Management',
                },
            },
            {
                path: "quotes-sent",
                name: "admin-quotes-sent",
                component: AdminQuoteSent,
                meta: {
                    title: 'shopexperts - Admin Quotes',
                },
            },
            {
                path: "transactions",
                name: "admin-transactions",
                component: AdminTransactions,
                meta: {
                    title: 'shopexperts - Admin Transactions',
                },
            },
            {
                path: "referrals",
                name: "admin-referrals",
                component: AdminReferrals,
                meta: {
                    title: 'shopexperts - Admin Referrals',
                },
            },
            {
                path: "apps-themes",
                name: "admin-apps-themes",
                component: AdminAppsThemes,
                meta: {
                    title: 'shopexperts - Admin App and Theme Skills',
                },
            },
            {
                path: "expert-subscriptions",
                name: "admin-expert-subscriptions",
                component: AdminExpertSubscriptions,
                meta: {
                    title: 'shopexperts - Expert Subscriptions',
                },
            },
            {
                path: "expert-plans",
                redirect: { name: "admin-expert-subscriptions" },
            },
            {
                path: "expert-levels",
                name: "admin-expert-levels",
                component: AdminExpertLevels,
                meta: {
                    title: 'shopexperts - Expert Levels',
                },
            },
            {
                path: "wallet-transactions",
                name: "admin-wallet-transactions",
                component: AdminWalletTransactions,
                meta: {
                    title: 'shopexperts - Wallet Transactions',
                },
            },
            {
                path: "reviews",
                name: "admin-reviews",
                component: AdminReviews,
                meta: {
                    title: 'shopexperts - Admin Reviews',
                },
            },
            {
                path: "partner-directory",
                name: "admin-partner-directory",
                component: AdminPartnerDirectoryScraper,
                meta: {
                    title: 'shopexperts - Partner Directory Scraper',
                    hideSideNavigation: true,
                },
            },
            {
                path: "automations",
                name: "admin-automations",
                component: () => import("@/app/pages/automations/AutomationsPage.vue"),
                meta: { title: 'shopexperts - Automations' },
            },
            {
                path: "pages",
                name: "admin-pages",
                component: AdminPages,
                meta: {
                    title: 'shopexperts - Admin Pages',
                },
            },
            {
                path: "customized-pages/create",
                name: "admin-customized-pages-create",
                component: AdminCustomizedPages,
                meta: {
                    title: 'shopexperts - Create Customized Page',
                    hideSideNavigation: true,
                },
            },
            {
                path: "customized-pages/:pageId/edit",
                name: "admin-customized-pages-edit",
                component: AdminCustomizedPages,
                meta: {
                    title: 'shopexperts - Edit Customized Page',
                    hideSideNavigation: true,
                },
            },
            {
                path: "customized-pages",
                name: "admin-customized-pages",
                component: AdminCustomizedPages,
                meta: {
                    title: 'shopexperts - Customized Pages',
                },
            },
            {
                path: "page-collections",
                name: "admin-page-collections",
                component: AdminPageCollections,
                meta: {
                    title: 'shopexperts - Page Collections',
                },
            },
            {
                path: "page-collections/create",
                name: "admin-page-collections-create",
                component: AdminPageCollectionEditor,
                meta: {
                    title: 'shopexperts - Create Page Collection',
                },
            },
            {
                path: "page-collections/:collectionId/edit",
                name: "admin-page-collections-edit",
                component: AdminPageCollectionEditor,
                meta: {
                    title: 'shopexperts - Edit Page Collection',
                },
            },
            {
                path: "page-schemas/:schemaId/edit",
                name: "admin-page-schema-builder",
                component: AdminSchemaBuilder,
                meta: {
                    title: 'shopexperts - Schema Builder',
                    hideSideNavigation: true,
                },
            },
            {
                path: "pages/:schemaId/editor",
                name: "admin-page-metafields-editor",
                component: AdminPageMetafieldsEditor,
                meta: {
                    title: 'shopexperts - Page Metafields Editor',
                    hideSideNavigation: true,
                },
            },
            {
                path: "pages/:schemaId/editor/:pageId",
                name: "admin-page-metafields-editor-page",
                component: AdminPageMetafieldsEditor,
                meta: {
                    title: 'shopexperts - Page Metafields Editor',
                    hideSideNavigation: true,
                },
            },
            // The card/aside schema detail page was replaced by the page
            // editor; keep old links working instead of 404ing them.
            {
                path: "pages/:schemaId",
                name: "admin-pages-detail",
                redirect: (to: RouteLocationNormalized) => ({
                    name: "admin-page-metafields-editor",
                    params: { schemaId: to.params.schemaId },
                }),
            } as RouteRecordRaw,
            {
                path: "redirects",
                name: "admin-redirects",
                component: AdminRedirects,
                meta: {
                    title: 'shopexperts - Admin Redirects',
                },
            },
            {
                path: "email-preview",
                name: "admin-email-preview",
                component: AdminEmailPreview,
                meta: {
                    title: 'shopexperts - Email Preview',
                },
            },
            {
                path: "expert-manager",
                name: "admin-expert-manager",
                component: AdminExpertManager,
                meta: {
                    title: 'shopexperts - Expert Manager',
                },
            },
            {
                path: "expert-manager/:expertId",
                name: "admin-expert-detail",
                component: AdminExpertDetail,
                meta: {
                    title: 'shopexperts - Expert Details',
                },
            },
            {
                path: "expert-claims",
                name: "admin-expert-claims",
                component: AdminExpertClaimsPage,
                meta: {
                    title: 'shopexperts - Expert Claims',
                },
            },
            {
                path: "confidence-signals",
                name: "admin-confidence-signals",
                component: AdminConfidenceSignals,
                meta: {
                    title: 'shopexperts - Trust Signals',
                },
            },
            {
                path: "categories-manager",
                name: "admin-categories-manager",
                component: AdminCategoriesManager,
                meta: {
                    title: 'shopexperts - Categories Manager',
                },
            },
            {
                path: "satellite-sites",
                name: "admin-satellite-sites",
                component: AdminSatelliteContactSites,
                meta: {
                    title: 'shopexperts - Satellite Contact Sites',
                },
            },
            {
                path: "ban-list",
                name: "admin-ban-list",
                component: AdminBanList,
                meta: {
                    title: 'shopexperts - Ban List',
                },
            },
            {
                path: "emails",
                name: "admin-emails",
                component: AdminEmails,
                meta: {
                    title: 'shopexperts - Emails',
                },
            },
        ]
    },
    {
        path: "/client",
        component: ClientDashboardLayout,
        meta: {
            title: 'shopexperts - Client Dashboard',
            requiresAuth: true,
            requiredRole: 'client'
        },
        children: [
            {
                path: "",
                redirect: () => getClientHomePath(),
            },
            {
                path: "dashboard",
                redirect: () => getClientHomePath(),
            },
            {
                path: "overview",
                name: "client-overview",
                component: ClientOverview,
                meta: {
                    title: 'shopexperts - Overview',
                },
            },
            {
                path: "leads",
                name: "client-leads",
                component: ClientLeads,
                beforeEnter: redirectToProjectsWhenEnabled("/client/projects"),
                meta: {
                    title: 'shopexperts - My Requests',
                },
            },
            {
                // Entry point from the website's "Send a message"; must precede messages/:threadId.
                path: "messages/new",
                name: "client-messages-new",
                component: DirectMessageStartPage,
                beforeEnter: requireV2Projects,
                meta: { title: 'shopexperts - Messages' },
            },
            {
                path: "messages/:threadId?",
                name: "client-messages",
                component: DirectMessagesPage,
                beforeEnter: requireV2Projects,
                meta: { title: 'shopexperts - Messages' },
            },
            {
                path: "projects",
                name: "client-projects",
                component: ProjectsListPage,
                beforeEnter: requireV2Projects,
                meta: {
                    title: 'shopexperts - Projects',
                },
            },
            {
                path: "projects/:id",
                name: "client-project-details",
                component: ProjectDetailPage,
                beforeEnter: requireV2Projects,
                meta: {
                    title: 'shopexperts - Project Details',
                },
            },
            {
                path: "reviews",
                name: "client-reviews",
                component: ClientReviews,
                meta: {
                    title: 'shopexperts - Reviews',
                },
            },
            {
                path: "reviews/new/:expertSlug",
                name: "client-direct-review",
                component: ClientDirectReview,
                meta: {
                    title: 'shopexperts - Write a Review',
                },
            },
            {
                path: "settings",
                name: "client-settings",
                component: ClientSettings,
                meta: {
                    title: 'shopexperts - Settings',
                },
            },
        ]
    },

    // Public email verification routes
    {
        path: "/verify-email-change",
        name: "verify-email-change",
        component: VerifyEmailChange,
        meta: {
            title: 'shopexperts - Verify Email Change',
        },
    },
    {
        path: "/verify-email",
        name: "verify-email",
        component: VerifyEmail,
        meta: {
            title: 'shopexperts - Verify Email',
        },
    },
    {
        path: "/claim-profile/verify",
        name: "claim-verify",
        component: ClaimVerify,
        meta: {
            title: 'shopexperts - Verify your claim',
        },
    },

    // Legacy claim routes: the /expert/claim-profile pages were removed, but
    // their URLs were previously distributed (crawler bridge payload, emails).
    // Redirect records resolve before the beforeEach guard runs, so these are
    // safe for unauthenticated visitors.
    {
        path: "/expert/claim-profile",
        redirect: "/",
    },
    {
        path: "/expert/claim-profile/verify",
        redirect: (to: RouteLocation) => ({ path: "/claim-profile/verify", query: to.query }),
    },
    {
        path: "/expert/claim-profile/:pathMatch(.*)*",
        redirect: "/",
    },

    // Legacy client routes (commented out)
    // {
    //     path: "/client",
    //     component: ClientLayout,
    //     meta: {
    //         title: 'shopexperts - Client Dashboard',
    //         requiresAuth: true,
    //         requiredRole: 'client'
    //     },
    //     children: [
    //         {
    //             path: "dashboard",
    //             name: "client-dashboard",
    //             component: ClientDashboard,
    //             meta: {
    //                 title: 'shopexperts - Client Dashboard',
    //             },
    //         },
    //         {
    //             path: "my-requests",
    //             name: "my-requests",
    //             component: ClientRequest,
    //             meta: {
    //                 title: 'shopexperts - Client Requests',
    //             },
    //         },
    //         {
    //             path: '/client/request/:requestId',
    //             name: 'RequestDetails',
    //             meta: {
    //                 title: 'shopexperts - Request Details',
    //             },
    //             component: () => import('@/pages/client/RequestDetail.vue'),
    //             props: true
    //         },
    //         {
    //             path: "packaged-services",
    //             name: "packaged-services",
    //             component: PackagedServices,
    //             meta: {
    //                 title: 'shopexperts - Client Packaged Services',
    //             },
    //         },
    //         {
    //             path: "reviews",
    //             name: "reviews",
    //             component: Reviews,
    //             meta: {
    //                 title: 'shopexperts - Client Reviews',
    //             },
    //         },
    //         {
    //             path: "transactions",
    //             name: "client-transactions",
    //             component: ClientTransactions,
    //             meta: {
    //                 title: 'shopexperts - Client Transactions',
    //             },
    //         },
    //         {
    //             path: "settings",
    //             name: "client-settings",
    //             component: ClientSettings,
    //             meta: {
    //                 title: 'shopexperts - Client Settings',
    //             },
    //         },
    //     ]
    // }
];

const router = createRouter({
    history: createWebHistory(),
    routes,
});

// TODO: Refactor this mess
router.beforeEach((to, _from, next) => {
    const authStore = useAuthStore()
    
    const exactPublicRoutes = [
        '/',
        '/login',
        '/forgot-password', 
        '/reset-password',
        '/create-password',
        '/accept-invite',
        '/sso-login',
        '/logout',
        '/verify-email-change',
        '/verify-email',
        '/claim-profile/verify',
        // Tolerate a trailing slash on the emailed claim link (the route
        // itself matches either form since strict matching is off).
        '/claim-profile/verify/'
    ]
    
    const publicPathPrefixes = [
        '/expert/signup',
        '/client/signup', 
        '/client/free-quote', 
        '/client/get-matched',
        '/review/invite',
        '/get-free-quote',
        '/create-project',
        '/embedded/'
    ]
    
    const isExactPublicRoute = exactPublicRoutes.includes(to.path)
    const isPublicPathPrefix = publicPathPrefixes.some(prefix => to.path.startsWith(prefix))
    const isPublicRoute = isExactPublicRoute || isPublicPathPrefix
    
    if (isPublicRoute) {
        if (authStore.token && authStore.user && (to.path.includes('/login') || to.path === '/')) {
            if (authStore.user.role_id === 1) {
                next('/admin/dashboard')
                return
            } else if (authStore.user.role_id === 2) {
                next(getClientHomePath())
                return
            } else if (authStore.user.role_id === 3) {
                next('/expert/dashboard')
                return
            }
        }
        
        updateDocumentMeta(to);
        next();
        return
    }
    
    const isAdminRoute = to.path.startsWith('/admin')
    const isExpertRoute = to.path.startsWith('/expert')
    const isClientRoute = to.path.startsWith('/client')
    const isCheckoutRoute = to.path === '/checkout'
    const isProtectedRoute = isAdminRoute || isExpertRoute || isClientRoute || isCheckoutRoute
    
    if (isProtectedRoute) {
        if (!authStore.token || !authStore.user) {
            const invitationToken = parseBillingInvitationToken(to.hash)
            if (to.path === '/expert/settings') {
                if (invitationToken) {
                    holdBillingInvitationTokenForLogin(invitationToken)
                }
            }
            // Never place an invitation secret inside the login redirect query,
            // including on aliases or paths that do not ultimately accept it.
            const redirect = stripBillingInvitationFromFullPath(to.fullPath)
            next({ path: '/login', query: { redirect } })
            return
        }
        
        const userRoleId = authStore.user.role_id
        
        if (isAdminRoute && userRoleId !== 1) {
            if (userRoleId === 2) {
                next(getClientHomePath())
                return
            } else if (userRoleId === 3) {
                next('/expert/dashboard')
                return
            }
            next('/')
            return
        } 
        
        if (isExpertRoute && userRoleId !== 3) {
            if (userRoleId === 1) {
                next('/admin/dashboard')
                return
            } else if (userRoleId === 2) {
                next(getClientHomePath())
                return
            }
            next('/')
            return
        } 
        
        if ((isClientRoute || isCheckoutRoute) && userRoleId !== 2) {
            if (userRoleId === 1) {
                next('/admin/dashboard')
                return
            } else if (userRoleId === 3) {
                next('/expert/dashboard')
                return
            }
            next('/')
            return
        }
    } else {
        next('/login')
        return
    }
    
    updateDocumentMeta(to);
    next();
})

export default router
